import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Sparkles, Save, History } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const SECTIONS = [
  { key: "deposit_policy", label: "Deposit policy" },
  { key: "cancellation_policy", label: "Cancellation policy" },
  { key: "late_return_policy", label: "Late return policy" },
  { key: "fuel_policy", label: "Fuel/charge policy" },
  { key: "damage_policy", label: "Damage policy" },
  { key: "travel_restrictions", label: "Travel restrictions" },
  { key: "other_terms", label: "Other rental terms" },
];

const EMPTY_SECTIONS = Object.fromEntries(SECTIONS.map((s) => [s.key, ""]));

export default function TermsSettings() {
  const [draft, setDraft] = useState(EMPTY_SECTIONS);
  const [current, setCurrent] = useState(null); // currently published version, or null
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, v] = await Promise.all([
        api.get("/company/terms/draft"),
        api.get("/company/terms/current"),
        api.get("/company/terms/versions"),
      ]);
      setDraft({ ...EMPTY_SECTIONS, ...(d.data.sections || {}) });
      setCurrent(c.data.version);
      setVersions(v.data.versions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put("/company/terms/draft", draft);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const useTemplate = async () => {
    try {
      const { data } = await api.get("/company/terms/template");
      setDraft({ ...EMPTY_SECTIONS, ...(data.sections || {}) });
      toast.success("Template loaded into the draft below: review and edit before publishing.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/company/terms/publish", draft);
      toast.success(`Terms Version ${data.version_number} published`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPublishing(false);
    }
  };

  const draftMatchesCurrent = current && SECTIONS.every((s) => (draft[s.key] || "") === (current.sections?.[s.key] || ""));

  if (loading) {
    return <div className="max-w-4xl text-sm text-slate-500">Loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="terms-settings-page">
      <Link to="/app/settings" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to Settings
      </Link>

      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Bookings</div>
        <h1 className="font-display text-3xl font-bold mt-1 inline-flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#1B4332]" /> Booking terms
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          The policies customers review and acknowledge before confirming a booking. Editing below only
          changes your working draft: nothing customers see changes until you <b>Publish</b>. Once published,
          a version is locked forever: bookings already confirmed keep showing exactly what they agreed to,
          even after you publish a newer version.
        </p>
        <p className="text-xs text-slate-500 mt-2">
          This is a customer-facing terms acknowledgment, not a legally reviewed contract: write these to match
          your actual business policies.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="terms-current-status">
        {current ? (
          <div className="text-sm text-slate-700">
            Currently published: <span className="font-semibold">Version {current.version_number}</span>
            {" · "}{new Date(current.published_at).toLocaleString()}
          </div>
        ) : (
          <div className="text-sm text-slate-500">No terms have been published yet: customers won't see a terms section at confirmation until you publish one.</div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-display text-lg font-semibold">Draft</h2>
          <button onClick={useTemplate} data-testid="terms-use-template-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
            <Sparkles className="w-4 h-4" /> Start from template
          </button>
        </div>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Template wording is a generic starting point: review and edit these terms to match your actual
          business policies before publishing.
        </p>

        <div className="space-y-4">
          {SECTIONS.map((s) => (
            <label key={s.key} className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{s.label}</span>
              <textarea rows={3} value={draft[s.key] || ""}
                        data-testid={`terms-section-${s.key}`}
                        onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
                        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={saveDraft} disabled={saving} data-testid="terms-save-draft-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save draft"}
          </button>
          <button onClick={publish} disabled={publishing || draftMatchesCurrent} data-testid="terms-publish-btn"
                  title={draftMatchesCurrent ? "Draft matches the currently published version" : ""}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white hover:opacity-90 text-sm disabled:opacity-50">
            {publishing ? "Publishing…" : `Publish new version${current ? ` (v${current.version_number + 1})` : " (v1)"}`}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-base font-semibold inline-flex items-center gap-1">
          <History className="w-4 h-4" /> Version history
        </h2>
        <ol className="mt-3 space-y-2 text-sm" data-testid="terms-version-history">
          {versions.length === 0 ? (
            <li className="text-slate-500 italic">No versions published yet.</li>
          ) : versions.map((v) => (
            <li key={v.id} className="rounded-md border border-slate-200 bg-slate-50 p-2 flex items-center justify-between"
                data-testid={`terms-version-${v.version_number}`}>
              <span className="font-medium">Version {v.version_number}</span>
              <span className="text-xs text-slate-500">{new Date(v.published_at).toLocaleString()}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
